import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Box, Tooltip } from '@mui/material';

import {
  ColumnDef,
  CellContext,
  useReactTable,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { parse } from 'papaparse';

import { Constants } from '@arrived/common';

import { propertyNameAccessor, Table } from '../Table';

import { AwaitingValuation, AwaitingValuationDescription } from '../common/formattedMessages';
import { useIsMobile } from '../hooks';
import { NavigationBarContext } from '../navigation';
import { CellBodyTypography } from '../Portfolio/common/cells';
import { HistoricalPerformance, historicalPerformanceData } from './historicalPerformance';

const AppreciationCell = ({ row, ...rest }: CellContext<HistoricalPerformance, unknown>) => {
  const hasSharePriceBeenUpdated = useMemo(
    () => Constants.sharePriceUpdatedOfferings.includes(row.original.id),
    [row.original.id]
  );

  return (
    <>
      {hasSharePriceBeenUpdated ? (
        <PercentCell row={row} {...rest} />
      ) : (
        <CellBodyTypography>
          <Tooltip sx={{ cursor: 'pointer', textDecoration: 'underline' }} title={<AwaitingValuationDescription />}>
            <span>
              <AwaitingValuation />
            </span>
          </Tooltip>
        </CellBodyTypography>
      )}
    </>
  );
};

const PercentCell = ({ getValue }: CellContext<HistoricalPerformance, unknown>) => {
  const value = getValue() as number;

  return (
    <CellBodyTypography>
      <FormattedNumber value={value} maximumFractionDigits={1} minimumFractionDigits={1} style="percent" />
    </CellBodyTypography>
  );
};

const MoneyCell = ({ getValue }: CellContext<HistoricalPerformance, unknown>) => {
  const value = getValue() as number;
  return (
    <CellBodyTypography>
      <FormattedNumber value={value} style="currency" currency="USD" />
    </CellBodyTypography>
  );
};

const DefaultCell = ({ getValue }: CellContext<HistoricalPerformance, unknown>) => (
  <CellBodyTypography>{getValue() as string | number}</CellBodyTypography>
);

const createColumns = (isMobile: boolean): ColumnDef<HistoricalPerformance>[] => [
  {
    header: () => <FormattedMessage id="property-name" defaultMessage="Property Name" />,
    id: 'propertyName',
    accessorFn: (row) => propertyNameAccessor(row.name),
    cell: DefaultCell,
    size: 100,
  },
  {
    header: () => <FormattedMessage id="market" defaultMessage="Market" />,
    accessorKey: 'market',
    cell: DefaultCell,
    size: 100,
  },
  {
    header: () => <FormattedMessage id="months-held" defaultMessage="Months Held" />,
    accessorKey: 'monthsHeld',
    cell: DefaultCell,
    sortingFn: (rowA, rowB) => rowA.original.monthsHeld - rowB.original.monthsHeld,
    size: isMobile ? 100 : 70,
  },
  {
    header: () => <FormattedMessage id="total-returns" defaultMessage="Total Returns" />,
    accessorKey: 'totalReturns',
    cell: PercentCell,
    sortingFn: (rowA, rowB) => rowA.original.totalReturns - rowB.original.totalReturns,
    size: isMobile ? 100 : 120,
  },
  {
    header: () => <FormattedMessage id="annualized-appreciation" defaultMessage="All Time Appreciation" />,
    accessorKey: 'appreciationReturns',
    cell: AppreciationCell,
    sortingFn: (rowA, rowB) => (rowA.original.appreciationReturns ?? 0) - (rowB.original.appreciationReturns ?? 0),
    id: 'appreciationReturns',
    size: 120,
  },
  {
    header: () => <FormattedMessage id="annualized-rental-income" defaultMessage="Annualized Rental Income" />,
    accessorKey: 'rentalIncomeReturns',
    cell: PercentCell,
    sortingFn: (rowA, rowB) => rowA.original.rentalIncomeReturns - rowB.original.rentalIncomeReturns,
    id: 'rentalIncomeReturns',
    size: 120,
  },
  {
    header: () => <FormattedMessage id="share-price" defaultMessage="Share Price" />,
    accessorKey: 'sharePrice',
    cell: MoneyCell,
    sortingFn: (rowA, rowB) => rowA.original.sharePrice - rowB.original.sharePrice,
    size: 120,
    id: 'sharePrice',
  },
];

export interface PerformanceTableProps {
  className?: string;
}

export const PerformanceTable = ({ className }: PerformanceTableProps) => {
  const isMobile = useIsMobile();
  // useFixedNavBar();
  const { offset, transition } = useContext(NavigationBarContext);

  const data = useMemo(() => {
    return parse(historicalPerformanceData, { dynamicTyping: true, header: true }).data as HistoricalPerformance[];
  }, [historicalPerformanceData]);

  const columns = useMemo(() => createColumns(isMobile), [isMobile]);

  const [sortState, setSortState] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const table = useReactTable<HistoricalPerformance>({
    columns,
    data,
    state: {
      sorting: sortState,
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSortState,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    table
      .getAllLeafColumns()
      .filter(
        (column) =>
          column.id === 'rentalIncomeReturns' || column.id === 'appreciationReturns' || column.id === 'sharePrice'
      )
      .forEach((column) => column.toggleVisibility(!isMobile));
  }, [isMobile]);

  return (
    <Box
      sx={{
        '& .MuiTableRow-root': {
          '&:not(.MuiTableRow-head) .MuiTableCell-root': { py: 2 },
          '& .MuiTableCell-root:first-of-type': { pl: 2.5 },
        },
        '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
          borderBottomWidth: 0,
        },
      }}
    >
      <Table
        className={className}
        table={table}
        TableHeadProps={{
          sx: {
            backgroundColor: 'background.iceGray',
            position: 'sticky',
            top: offset,
            zIndex: 'mobileStepper',
            '& .MuiTableCell-root': {
              py: 2,
              borderBottomWidth: 0,
            },
          },
          style: {
            transition: `top ${transition}`,
          },
        }}
      />
    </Box>
  );
};
